import React from 'react';
import { Lightbulb, Plus, Trash2 } from 'lucide-react';

export interface LightingScheduleJob {
  id: string;
  snapshot: string;
}

export interface LightingScheduleConfirmModalProps {
  show: boolean;
  lightingScheduleSnapshot: string | null;
  lightingScheduleJobs: LightingScheduleJob[];
  onDeleteQueuedJob: (jobId: string) => void;
  onAddAndScanNext: () => void;
  onExtractAndDone: () => void;
  onCancel: () => void;
  onOverlayClick?: () => void;
  onSelectDifferent: () => void;
}

export const LightingScheduleConfirmModal: React.FC<LightingScheduleConfirmModalProps> = ({
  show,
  lightingScheduleSnapshot,
  lightingScheduleJobs,
  onDeleteQueuedJob,
  onAddAndScanNext,
  onExtractAndDone,
  onCancel,
  onOverlayClick,
  onSelectDifferent,
}) => {
  if (!show || !lightingScheduleSnapshot) return null;
  const handleOverlayClick = onOverlayClick ?? onCancel;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100002
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '28px',
          minWidth: '560px',
          maxWidth: '680px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid var(--border-color)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Lightbulb size={22} style={{ color: '#f59e0b' }} />
            Confirm Lighting Schedule
          </h3>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
            Add this schedule area to the queue or extract all queued schedules now.
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <img
            src={lightingScheduleSnapshot}
            alt="Selected lighting schedule area"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '300px',
              objectFit: 'contain',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
            }}
          />
        </div>

        {lightingScheduleJobs.length > 0 && (
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontWeight: 500 }}>
              Schdueles to Add ({lightingScheduleJobs.length})
            </label>
            <div
              style={{
                maxHeight: 144,
                overflowY: 'auto',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                background: 'var(--bg-secondary)',
              }}
            >
              {lightingScheduleJobs.map((job, idx) => (
                <div
                  key={job.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    borderBottom: idx < lightingScheduleJobs.length - 1 ? '1px solid var(--border-color)' : 'none',
                  }}
                >
                  <img
                    src={job.snapshot}
                    alt={`Queued schedule ${idx + 1}`}
                    style={{
                      width: 56,
                      height: 38,
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: '1px solid var(--border-strong)',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                    Schedule {idx + 1}
                  </span>
                  <button
                    onClick={() => onDeleteQueuedJob(job.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--border-strong)',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 4,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                    title="Remove queued schedule"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onSelectDifferent}
            style={{
              padding: 0,
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              marginRight: 'auto',
            }}
          >
            Try Again
          </button>
          <button
            onClick={onAddAndScanNext}
            style={{
              padding: 0,
              background: 'transparent',
              border: 'none',
              color: '#3b82f6',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Plus size={16} />
            <span>Add Schedule</span>
          </button>
          <button
            onClick={onExtractAndDone}
            className="confirm-extract-btn"
            style={{
              padding: 0,
              background: 'transparent',
              border: 'none',
              color: '#f59e0b',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Lightbulb size={16} />
            <span>Add to Takeoff</span>
          </button>
        </div>
      </div>
    </div>
  );
};
